/**
 * Lockable Field Wrapper for Formly
 *
 * A Formly wrapper that adds real-time collaboration features:
 * - Ably-based field locking
 * - SSE update animation
 * - Visual lock indicator
 *
 * Zoneless Compatible:
 * - Uses RxJS timer() for async operations
 * - Uses signals for reactive state
 * - Properly triggers change detection
 *
 * Usage in field config:
 * {
 *   key: 'title',
 *   type: 'input',
 *   wrappers: ['lockable-field'],
 *   props: {
 *     lockId: 'course-details-title',
 *     lockEnabled: true,
 *   }
 * }
 */
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  ViewChild,
  ElementRef,
  afterNextRender,
  Injector,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FieldWrapper, FormlyModule } from '@ngx-formly/core';
import { EMPTY, Subject, Subscription, catchError, takeUntil, timer } from 'rxjs';
import { AblySpacesService } from '../services/ably-spaces.service';
import { LockInfo } from '../models/collaboration.models';

@Component({
  selector: 'formly-wrapper-lockable-field',
  standalone: true,
  imports: [FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      #fieldContainer
      class="lockable-field-wrapper"
      [class.is-locked-by-other]="isLockedByOther()"
      [class.is-editing-by-me]="isEditingByMe()"
      [class.is-updating]="isUpdating()"
      [attr.data-lock-id]="lockId"
    >
      <!-- Lock indicator badge -->
      @if (isLockedByOther()) {
        <div class="lock-badge" [style.background-color]="lockInfo()?.memberColor">
          <svg class="lock-icon" viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
          </svg>
          <span class="lock-user-name">{{ lockInfo()?.memberName }} is editing</span>
        </div>
      }

      <!-- Update indicator badge -->
      @if (isUpdating()) {
        <div class="update-badge" [style.background-color]="updateUserColor()">
          <svg class="update-icon" viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
            <path d="M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1-2.73 2.71-2.73 7.08 0 9.79s7.15 2.71 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58s9.14-3.47 12.65 0L21 3v7.12z"/>
          </svg>
          <span class="update-user-name">{{ updateUserName() }} updated</span>
        </div>
      }

      <!-- Editing indicator (blue dot when I'm editing) -->
      @if (isEditingByMe()) {
        <div class="my-edit-indicator">
          <div class="pulse-dot"></div>
        </div>
      }

      <!-- The actual field -->
      <div class="field-content" [class.disabled]="isLockedByOther()">
        <ng-container #fieldComponent></ng-container>
      </div>
    </div>
  `,
  styles: [`
    .lockable-field-wrapper {
      position: relative;
      transition: all 0.2s ease;
    }

    /* Lock badge - at right upper edge of input border */
    .lock-badge {
      position: absolute;
      top: 22px;
      right: 8px;
      transform: translateY(-50%);
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: 4px;
      color: white;
      font-size: 11px;
      font-weight: 500;
      z-index: 10;
      animation: fadeIn 0.2s ease;
    }

    .lock-icon {
      flex-shrink: 0;
      width: 12px;
      height: 12px;
    }

    .lock-user-name {
      max-width: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Update badge - at right upper edge of input border */
    .update-badge {
      position: absolute;
      top: 22px;
      right: 8px;
      transform: translateY(-50%);
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: 4px;
      color: white;
      font-size: 11px;
      font-weight: 500;
      z-index: 10;
      animation: fadeIn 0.3s ease;
    }

    .update-icon {
      flex-shrink: 0;
      width: 12px;
      height: 12px;
      animation: spin 1s linear infinite;
    }

    .update-user-name {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* My editing indicator - inject into the label using ::ng-deep */
    .lockable-field-wrapper.is-editing-by-me ::ng-deep label {
      position: relative;
      display: inline-flex;
      align-items: center;
    }

    .lockable-field-wrapper.is-editing-by-me ::ng-deep label::after {
      content: '';
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #3b82f6;
      margin-left: 6px;
      flex-shrink: 0;
      animation: pulse 1.5s ease-in-out infinite;
    }

    /* Hide the separate indicator div since we're using ::after on label */
    .my-edit-indicator {
      display: none;
    }

    .pulse-dot {
      display: none;
    }

    /* Locked state - subtle border on input elements */
    .lockable-field-wrapper.is-locked-by-other ::ng-deep input,
    .lockable-field-wrapper.is-locked-by-other ::ng-deep textarea,
    .lockable-field-wrapper.is-locked-by-other ::ng-deep select {
      border-color: var(--lock-color, #ef4444) !important;
    }

    .field-content.disabled {
      pointer-events: none;
      opacity: 0.85;
    }

    .field-content.disabled ::ng-deep input,
    .field-content.disabled ::ng-deep textarea,
    .field-content.disabled ::ng-deep select {
      cursor: not-allowed !important;
      background-color: rgba(0, 0, 0, 0.02) !important;
    }

    /* Editing state - subtle border on input elements */
    .lockable-field-wrapper.is-editing-by-me ::ng-deep input,
    .lockable-field-wrapper.is-editing-by-me ::ng-deep textarea,
    .lockable-field-wrapper.is-editing-by-me ::ng-deep select {
      border-color: #3b82f6 !important;
    }

    /* Update animation */
    .lockable-field-wrapper.is-updating ::ng-deep input,
    .lockable-field-wrapper.is-updating ::ng-deep textarea {
      animation: highlightField 1.5s ease;
    }

    /* Animations */
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.5;
        transform: scale(1.4);
      }
    }

    @keyframes highlightField {
      0% { background-color: transparent; }
      15% { background-color: rgba(59, 130, 246, 0.1); }
      100% { background-color: transparent; }
    }
  `]
})
export class LockableFieldWrapper extends FieldWrapper implements OnInit, OnDestroy {
  private readonly ablySpaces = inject(AblySpacesService);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('fieldContainer') fieldContainer!: ElementRef;

  // State signals
  lockInfo = signal<LockInfo | null>(null);
  isEditingByMe = signal(false);
  isUpdating = signal(false);
  updateUserName = signal('');
  updateUserColor = signal('#3b82f6');

  // Computed
  isLockedByOther = computed(() => {
    const lock = this.lockInfo();
    return lock !== null && !lock.isLockedByMe;
  });

  // Subscriptions for zoneless-compatible timers
  private updateTimerSub: Subscription | null = null;
  private blurTimerSub: Subscription | null = null;
  private setupTimerSub: Subscription | null = null;

  // Edge case handling (matching intended learners quality)
  private isLockOwned = false; // Track if we own the lock
  private isPendingRelease = false; // Track if release is in progress
  private isAcquiring = false; // Track if acquisition is in progress
  private currentInputElement: HTMLInputElement | HTMLTextAreaElement | null = null;

  // Event listener cleanup tracking (prevent memory leaks)
  private eventListeners: Array<{
    element: HTMLElement | Element;
    event: string;
    handler: EventListener;
  }> = [];

  get lockId(): string {
    return this.props['lockId'] || `field-${this.field.key}`;
  }

  get lockEnabled(): boolean {
    return this.props['lockEnabled'] !== false;
  }

  ngOnInit(): void {
    if (!this.lockEnabled) return;

    // SSR guard: Skip browser-only setup
    if (!this.isBrowser) return;

    // Use afterNextRender to ensure view is ready before subscribing to locks
    afterNextRender(() => {
      // Subscribe to lock changes
      this.ablySpaces.locks$.pipe(
        catchError((error) => {
          console.error('[LockableField] Error in locks stream:', error);
          return EMPTY; // Complete the stream gracefully
        }),
        takeUntil(this.destroy$)
      ).subscribe((locks) => {
        const lock = locks.get(this.lockId);
        this.lockInfo.set(lock || null);

        // Update CSS variable for lock color
        if (lock && !lock.isLockedByMe) {
          this.fieldContainer?.nativeElement?.style.setProperty('--lock-color', lock.memberColor);
        }
      });

      // Set up focus/blur handlers after Formly renders (150ms delay)
      this.setupTimerSub = timer(150)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.setupFieldListeners();
        });
    }, { injector: this.injector });
  }

  /**
   * Show update animation - call this from component when SSE update is received
   */
  showUpdateAnimation(userName: string, userColor: string = '#3b82f6'): void {
    // Cancel any existing timer
    this.updateTimerSub?.unsubscribe();

    this.updateUserName.set(userName);
    this.updateUserColor.set(userColor);
    this.isUpdating.set(true);
    // Note: No markForCheck needed - signals trigger updates automatically

    // Use RxJS timer for zoneless compatibility
    this.updateTimerSub = timer(2500)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isUpdating.set(false);
        // Note: No markForCheck needed - signals trigger updates automatically
      });
  }

  private setupFieldListeners(): void {
    const container = this.fieldContainer?.nativeElement;
    if (!container) return;

    // Listen for native focus/blur on input elements
    const inputs = container.querySelectorAll('input, textarea, select');
    inputs.forEach((input: HTMLElement) => {
      const focusHandler = () => this.handleFocus(input);
      const blurHandler = () => this.handleBlur();

      input.addEventListener('focus', focusHandler);
      input.addEventListener('blur', blurHandler);

      // Store references for cleanup
      this.eventListeners.push({ element: input, event: 'focus', handler: focusHandler });
      this.eventListeners.push({ element: input, event: 'blur', handler: blurHandler });
    });

    // Listen for focusin/focusout on container to catch events from Shadow DOM
    // and contenteditable elements (like rich text editors)
    const focusinHandler = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Only handle if it's a contenteditable or inside Shadow DOM
      if (target?.getAttribute?.('contenteditable') === 'true' ||
          (e.composedPath && e.composedPath().some((el: any) => el?.getAttribute?.('contenteditable') === 'true'))) {
        this.handleFocus(target);
      }
    };
    const focusoutHandler = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target?.getAttribute?.('contenteditable') === 'true' ||
          (e.composedPath && e.composedPath().some((el: any) => el?.getAttribute?.('contenteditable') === 'true'))) {
        this.handleBlur();
      }
    };

    container.addEventListener('focusin', focusinHandler);
    container.addEventListener('focusout', focusoutHandler);

    // Store references for cleanup
    this.eventListeners.push({ element: container, event: 'focusin', handler: focusinHandler as EventListener });
    this.eventListeners.push({ element: container, event: 'focusout', handler: focusoutHandler as EventListener });

    // Listen for custom select dropdown events (for stylish/custom dropdowns)
    const selectOpenHandler = (e: Event) => {
      this.handleSelectOpen(e as CustomEvent);
    };
    const selectCloseHandler = () => {
      this.handleSelectClose();
    };

    container.addEventListener('formly-select-open', selectOpenHandler);
    container.addEventListener('formly-select-close', selectCloseHandler);

    // Store references for cleanup
    this.eventListeners.push({ element: container, event: 'formly-select-open', handler: selectOpenHandler });
    this.eventListeners.push({ element: container, event: 'formly-select-close', handler: selectCloseHandler });
  }

  private async handleSelectOpen(event: CustomEvent): Promise<void> {
    if (!this.lockEnabled) return;

    // Cancel any pending blur
    this.blurTimerSub?.unsubscribe();
    this.blurTimerSub = null;

    // Skip if we already own this lock
    if (this.isLockOwned) return;

    // Skip if release is in progress
    if (this.isPendingRelease) return;

    // Skip if already acquiring
    if (this.isAcquiring) return;

    // Check if locked by someone else
    const lockInfo = this.ablySpaces.getLockInfo(this.lockId);
    if (lockInfo && !lockInfo.isLockedByMe) {
      // Close the dropdown by dispatching a close event
      this.fieldContainer?.nativeElement?.dispatchEvent(
        new CustomEvent('formly-lock-blocked', { bubbles: true })
      );
      return;
    }

    this.isAcquiring = true;

    try {
      const acquired = await this.ablySpaces.acquireLock(this.lockId);
      if (acquired) {
        this.isLockOwned = true;
        this.isEditingByMe.set(true);
      } else {
        // Failed to acquire - close the dropdown
        this.fieldContainer?.nativeElement?.dispatchEvent(
          new CustomEvent('formly-lock-blocked', { bubbles: true })
        );
      }
    } finally {
      this.isAcquiring = false;
    }
  }

  private handleSelectClose(): void {
    if (!this.lockEnabled) return;

    // Don't schedule release if we don't own the lock
    if (!this.isLockOwned) return;

    // Don't schedule if release is already in progress
    if (this.isPendingRelease) return;

    // Cancel any existing blur timer
    this.blurTimerSub?.unsubscribe();

    // Delay release for rapid interactions
    this.blurTimerSub = timer(300)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        if (!this.isLockOwned) return;

        this.isPendingRelease = true;

        try {
          await this.ablySpaces.releaseLock(this.lockId);
        } finally {
          this.isLockOwned = false;
          this.isPendingRelease = false;
          this.isEditingByMe.set(false);
        }
      });
  }

  private async handleFocus(inputElement: HTMLElement): Promise<void> {
    if (!this.lockEnabled) return;

    // Store reference for blur check
    this.currentInputElement = inputElement as HTMLInputElement;

    // Cancel any pending blur (user refocused before release completed)
    this.blurTimerSub?.unsubscribe();
    this.blurTimerSub = null;

    // Skip if we already own this lock
    if (this.isLockOwned) {
      return;
    }

    // Skip if release is in progress (wait for it to complete)
    if (this.isPendingRelease) {
      return;
    }

    // Skip if already acquiring this lock (prevent duplicate requests)
    if (this.isAcquiring) {
      return;
    }

    // Check if locked by someone else
    const lockInfo = this.ablySpaces.getLockInfo(this.lockId);
    if (lockInfo && !lockInfo.isLockedByMe) {
      inputElement.blur();
      return;
    }

    // Mark as acquiring to prevent duplicate requests
    this.isAcquiring = true;

    try {
      // Try to acquire lock
      const acquired = await this.ablySpaces.acquireLock(this.lockId);
      if (acquired) {
        this.isLockOwned = true;
        this.isEditingByMe.set(true);
        // Note: No markForCheck needed - signals trigger updates automatically
      } else {
        inputElement.blur();
      }
    } finally {
      this.isAcquiring = false;
    }
  }

  private handleBlur(): void {
    if (!this.lockEnabled) return;

    // Don't schedule release if we don't own the lock
    if (!this.isLockOwned) {
      return;
    }

    // Don't schedule if release is already in progress
    if (this.isPendingRelease) {
      return;
    }

    // Cancel any existing blur timer (debounce)
    this.blurTimerSub?.unsubscribe();

    // Delay release for rapid focus changes (zoneless compatible)
    // This handles cases like clicking from one field to another
    this.blurTimerSub = timer(300)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        // Double-check we still own the lock
        if (!this.isLockOwned) {
          return;
        }

        // Double-check we don't have focus anymore (SSR-safe)
        // This catches edge cases where focus returned during the delay
        if (this.isBrowser && this.currentInputElement && document.activeElement === this.currentInputElement) {
          return;
        }

        // Mark as pending release to prevent duplicate releases
        this.isPendingRelease = true;

        try {
          await this.ablySpaces.releaseLock(this.lockId);
        } finally {
          // Clean up state regardless of success/failure
          this.isLockOwned = false;
          this.isPendingRelease = false;
          this.isEditingByMe.set(false);
          // Note: No markForCheck needed - signals trigger updates automatically
        }
      });
  }

  ngOnDestroy(): void {
    // Cancel all timers first to prevent any pending operations
    this.updateTimerSub?.unsubscribe();
    this.blurTimerSub?.unsubscribe();
    this.setupTimerSub?.unsubscribe();

    // Remove all event listeners to prevent memory leaks
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];

    // Release lock if we own it (use the tracking flag, not signal)
    // This ensures we release even if there was a race condition
    if (this.isLockOwned) {
      this.ablySpaces.releaseLock(this.lockId);
      this.isLockOwned = false;
    }

    // Clean up state
    this.isPendingRelease = false;
    this.isAcquiring = false;
    this.currentInputElement = null;

    this.destroy$.next();
    this.destroy$.complete();
  }
}
